import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { WhanauBottomNav } from "./components/WhanauBottomNav";
import api, { authEvents, parseResponse } from "./lib/api";

// Pages
import LandingPage from "./pages/LandingPage";
import HomePage from "./marketing/HomePage";
import HowItWorksPage from "./marketing/HowItWorksPage";
import PricingPage from "./marketing/PricingPage";
import AboutPage from "./marketing/AboutPage";
import ContactPage from "./marketing/ContactPage";
import JoinBetaPage from "./marketing/JoinBetaPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import OnboardingPage from "./pages/OnboardingPage";
import OnboardingFlowPage from "./pages/OnboardingFlowPage";
import DashboardPage from "./pages/DashboardPage";
import AllocationPage from "./pages/AllocationPage";
import TheFlowPage from "./pages/TheFlowPage";
import TheClosePage from "./pages/TheClosePage";
import GamePage from "./pages/GamePage";
import ReportsPage from "./pages/ReportsPage";
import MentorPage from "./pages/MentorPage";
import SettingsPage from "./pages/SettingsPage";
import RulesPage from "./pages/RulesPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import AuthCallback from "./pages/AuthCallback";
import VisionPanelPage from "./pages/VisionPanelPage";
import BetaDashboardPage from "./pages/BetaDashboardPage";
import BetaInsightsPage from "./pages/BetaInsightsPage";
import TheARCPage from "./pages/TheARCPage";
import CirclePage from "./pages/CirclePage";
import AdminAnalyticsPage from "./pages/AdminAnalyticsPage";
import AdminFeedbackPage from "./pages/AdminFeedbackPage";
import FounderDashboardPage from "./pages/FounderDashboardPage";
import HomeOSPage from "./pages/HomeOSPage";import AICommandCentrePage from "./pages/homeos/AICommandCentrePage";
import TodayHousePage from "./pages/homeos/TodayHousePage";
import MemoriesPage from "./pages/homeos/MemoriesPage";
import FamilyPlanPage from "./pages/homeos/FamilyPlanPage";
import { CalmModeProvider } from "./pages/homeos/CalmModeContext";
import FamilyPage from "./pages/homeos/FamilyPage";
import MemberPortalPage from "./pages/homeos/MemberPortalPage";
import MealsPage from "./pages/homeos/MealsPage";
import ChoresPage from "./pages/homeos/ChoresPage";
import CalendarPage from "./pages/homeos/CalendarPage";
import MoneyPage from "./pages/homeos/MoneyPage";
import SuppliesPage from "./pages/homeos/SuppliesPage";
import HealthPage from "./pages/homeos/HealthPage";
import DocumentsPage from "./pages/homeos/DocumentsPage";
import HomeOSSetupPage from "./pages/homeos/HomeOSSetupPage";
import WelcomeRoomPage from "./pages/homeos/WelcomeRoomPage";
import MoodPage from "./pages/homeos/MoodPage";
import CleaningPage from "./pages/homeos/CleaningPage";
import MaintenancePage from "./pages/homeos/MaintenancePage";
import ContactsPage from "./pages/homeos/ContactsPage";
import ResidenceJournalSpecimenPage from "./pages/homeos/ResidenceJournalSpecimenPage";
import WallDisplayPage from "./pages/homeos/WallDisplayPage";
import WeeklyReportPage from "./pages/homeos/WeeklyReportPage";
import RecipeBookPage from "./pages/homeos/RecipeBookPage";
import { HomeOSStateProvider } from "./pages/homeos/state/HomeOSStateProvider";
import PartnerOnboardingPage from "./pages/PartnerOnboardingPage";
import PrivacyPolicyPage from "./marketing/PrivacyPage";
import TermsOfServicePage from "./marketing/TermsPage";
import SecurityPolicyPage from "./marketing/SecurityPage";

// Email Action Pages
import InviteAcceptPage from "./pages/InviteAcceptPage";
import InviteDeclinePage from "./pages/InviteDeclinePage";
import ActionApprovePage from "./pages/ActionApprovePage";
import ActionDeclinePage from "./pages/ActionDeclinePage";
import { Component } from "react";

import { GuidanceProvider } from "./components/guidance/GuidanceProvider";
import { WelcomeOverlay } from "./components/guidance/WelcomeOverlay";
import { RhythmProvider } from "./components/rhythm/RhythmProvider";
import { PrivacyProvider } from "./components/privacy/PrivacyProvider";

// Global ErrorBoundary - prevents blank pages from unhandled errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'system-ui', maxWidth: '600px', margin: '4rem auto' }}>
          <h2 style={{ color: '#991b1b', marginBottom: '1rem' }}>Something went wrong</h2>
          <p style={{ color: '#525252', marginBottom: '1rem' }}>
            This page encountered an error. Please try refreshing, or go back to the dashboard.
          </p>
          <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '8px', fontSize: '13px', overflow: 'auto', color: '#991b1b', marginBottom: '1rem' }}>
            {this.state.error?.message || 'Unknown error'}
          </pre>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => window.location.href = '/dashboard'}
              style={{ padding: '0.5rem 1rem', background: '#1a4d3e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
              style={{ padding: '0.5rem 1rem', background: '#e5e5e5', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Storage keys
const STORAGE_KEYS = {
  THEME: 'arawealth-theme',
  THEME_MODE: 'arawealth-theme-mode',
  THEME_STYLE: 'arawealth-theme-style',
  HOUSEHOLD: 'arawealth-household',
  AUTH_CHECKED: 'arawealth-auth-checked'
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};

function ThemeProvider({ children }) {
  // Mode: light | dark | system
  const [mode, setMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.THEME_MODE) || 'system';
    }
    return 'system';
  });

  // Resolved mode: actual light/dark applied to DOM
  const [resolvedMode, setResolvedMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
      if (stored === 'light') return 'light';
      if (stored === 'dark') return 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  // Theme style / environment: calm | focus | energy | wealth | balance | minimal
  const [themeStyle, setThemeStyle] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEYS.THEME_STYLE) || 'calm';
    }
    return 'calm';
  });

  // Resolve mode when mode changes
  useEffect(() => {
    if (mode === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      setResolvedMode(mq.matches ? 'dark' : 'light');
    } else {
      setResolvedMode(mode);
    }
  }, [mode]);

  // Apply resolved mode class to DOM
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedMode);
    localStorage.setItem(STORAGE_KEYS.THEME_MODE, mode);
    localStorage.setItem(STORAGE_KEYS.THEME, resolvedMode);
  }, [mode, resolvedMode]);

  // Apply theme style data-attribute to DOM
  useEffect(() => {
    const root = window.document.documentElement;
    if (themeStyle === 'calm') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', themeStyle);
    }
    localStorage.setItem(STORAGE_KEYS.THEME_STYLE, themeStyle);
  }, [themeStyle]);

  // System preference listener
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (mode === 'system') {
        setResolvedMode(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode]);

  const toggleTheme = () => {
    setMode(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'light';
      return resolvedMode === 'light' ? 'dark' : 'light';
    });
  };

  return (
    <ThemeContext.Provider value={{
      mode, setMode,
      resolvedMode,
      themeStyle, setThemeStyle,
      environment: themeStyle,
      setEnvironment: setThemeStyle,
      theme: resolvedMode,
      setTheme: setMode,
      toggleTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [household, setHousehold] = useState(null);
  const [households, setHouseholds] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const isCheckingAuth = useRef(false);
  const householdIdRef = useRef(null);

  /**
   * Check authentication status and hydrate user + household state
   * This is the primary way to verify session on app load/refresh
   */
  const checkAuth = useCallback(async () => {
    // Prevent duplicate auth checks
    if (isCheckingAuth.current) return;
    isCheckingAuth.current = true;

    try {
      const response = await api.auth.me();
      
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setHouseholds(data.households || []);
        setAuthError(null);
        console.log("[Auth] User loaded:", data.email, "| Households:", data.households?.length || 0);
        
        // Restore or select household
        if (data.households?.length > 0) {
          const storedId = localStorage.getItem(STORAGE_KEYS.HOUSEHOLD);
          const found = data.households.find(h => h.household_id === storedId);
          const selectedHousehold = found || data.households[0];
          setHousehold(selectedHousehold);
          console.log("[Auth] Household selected:", selectedHousehold.household_id, selectedHousehold.name, "| stored:", storedId || "none");
          
          // Persist selection
          if (selectedHousehold) {
            localStorage.setItem(STORAGE_KEYS.HOUSEHOLD, selectedHousehold.household_id);
          }
          // Track session count for environment suggestions
          const sessionCount = parseInt(localStorage.getItem('arawealth-session-count') || '0', 10);
          localStorage.setItem('arawealth-session-count', String(sessionCount + 1));
        } else {
          // No households - clear stale localStorage to prevent 401 errors
          localStorage.removeItem(STORAGE_KEYS.HOUSEHOLD);
          setHousehold(null);
        }
        return true;
      } else {
        // Auth failed - clear state
        setUser(null);
        setHousehold(null);
        setHouseholds([]);
        return false;
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setHousehold(null);
      setHouseholds([]);
      return false;
    } finally {
      setLoading(false);
      isCheckingAuth.current = false;
    }
  }, []);

  /**
   * Fetch accounts for the current household — single source of truth.
   * Call after any account mutation (rename, reorder, hide, delete, add).
   */
  const refreshAccounts = useCallback(async () => {
    const hhId = householdIdRef.current;
    if (!hhId) { setAccounts([]); return; }
    try {
      const res = await api.accounts.list(hhId);
      if (res.ok) {
        const data = await res.json();
        setAccounts(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('[AuthProvider] Failed to refresh accounts:', e);
    }
  }, []);

  // Auto-fetch accounts whenever the active household changes
  useEffect(() => {
    householdIdRef.current = household?.household_id || null;
    if (household?.household_id) {
      refreshAccounts();
    } else {
      setAccounts([]);
    }
  }, [household?.household_id, refreshAccounts]);

  /**
   * Login with email and password
   */
  const login = async (email, password) => {
    const response = await api.auth.login(email, password);
    const data = await parseResponse(response);
    
    // Hydrate full user state
    await checkAuth();
    return data;
  };

  /**
   * Register new user
   */
  const register = async (name, email, password) => {
    const response = await api.auth.register(name, email, password);
    const data = await parseResponse(response);
    
    setUser({ user_id: data.user_id, email: data.email, name: data.name });
    return data;
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await api.auth.logout();
    } catch (e) {
      // Continue even if logout API fails
    }
    setUser(null);
    setHousehold(null);
    setHouseholds([]);
    setAccounts([]);
    localStorage.removeItem(STORAGE_KEYS.HOUSEHOLD);
  };

  /**
   * Switch active household
   */
  const switchHousehold = (hh) => {
    setHousehold(hh);
    localStorage.setItem(STORAGE_KEYS.HOUSEHOLD, hh.household_id);
  };

  /**
   * Initiate Google OAuth login
   */
  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to origin root - the auth callback handler will process session_id from URL hash
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  /**
   * Process OAuth session callback
   */
  const processOAuthSession = async (sessionId) => {
    const response = await api.auth.exchangeSession(sessionId);
    
    if (!response.ok) {
      throw new Error('Failed to process OAuth session');
    }
    
    const data = await response.json();
    await checkAuth();
    return data;
  };

  /**
   * Demo login for quick access
   */
  const demoLogin = async () => {
    const response = await api.auth.demo();
    
    if (!response.ok) {
      throw new Error('Demo login failed');
    }
    
    const data = await response.json();
    await checkAuth();
    return data;
  };

  // Initial auth check on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for auth errors from API client
  useEffect(() => {
    const unsubscribe = authEvents.subscribe((event) => {
      if (event.type === 'AUTH_ERROR') {
        setAuthError(event.message);
        setUser(null);
        setHousehold(null);
        setHouseholds([]);
      }
    });
    
    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      household,
      households,
      accounts,
      loading,
      authError,
      login,
      register,
      logout,
      loginWithGoogle,
      processOAuthSession,
      checkAuth,
      refreshAccounts,
      switchHousehold,
      demoLogin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading, household, authError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle auth errors - redirect to login
  useEffect(() => {
    if (authError && !loading) {
      navigate('/login', { state: { from: location }, replace: true });
    }
  }, [authError, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user has no household, redirect to onboarding
  if (!household && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();
  
  // Check for OAuth callback - session_id in URL fragment
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/join-beta" element={<JoinBetaPage />} />
      <Route path="/legacy-landing" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Email Action Routes - No auth required */}
      <Route path="/invite/accept" element={<InviteAcceptPage />} />
      <Route path="/invite/decline" element={<InviteDeclinePage />} />
      <Route path="/action/approve" element={<ActionApprovePage />} />
      <Route path="/action/decline" element={<ActionDeclinePage />} />
      
      {/* Public Security/Legal Pages - No auth required */}
      <Route path="/security" element={<SecurityPolicyPage />} />
      <Route path="/security/open-banking" element={<SecurityPolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/onboarding-flow" element={
        <ProtectedRoute>
          <OnboardingFlowPage />
        </ProtectedRoute>
      } />
      <Route path="/partner-onboarding" element={
        <ProtectedRoute>
          <PartnerOnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/plan" element={
        <ProtectedRoute>
          <AllocationPage />
        </ProtectedRoute>
      } />
      <Route path="/flow" element={
        <ProtectedRoute>
          <TheFlowPage />
        </ProtectedRoute>
      } />
      <Route path="/close" element={
        <ProtectedRoute>
          <TheClosePage />
        </ProtectedRoute>
      } />
      <Route path="/home-os" element={
        <ProtectedRoute>
          <HomeOSStateProvider><CalmModeProvider>
            <TodayHousePage />
          </CalmModeProvider></HomeOSStateProvider>
        </ProtectedRoute>
      } />
      <Route path="/home-os/legacy" element={
        <ProtectedRoute>
          <HomeOSStateProvider><CalmModeProvider>
            <HomeOSPage />
          </CalmModeProvider></HomeOSStateProvider>
        </ProtectedRoute>
      } />
      <Route path="/home-os/command-centre" element={
        <ProtectedRoute>
          <HomeOSStateProvider><CalmModeProvider>
            <AICommandCentrePage />
          </CalmModeProvider></HomeOSStateProvider>
        </ProtectedRoute>
      } />
      <Route path="/home-os/family" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><FamilyPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/family/:memberId" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><MemberPortalPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/mood" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><MoodPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/meals" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><MealsPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/chores" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><ChoresPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/cleaning" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><CleaningPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/maintenance" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><MaintenancePage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/wall" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><WallDisplayPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/report" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><WeeklyReportPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/meals/recipe-book" element={<ProtectedRoute><HomeOSStateProvider><RecipeBookPage /></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/calendar" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><CalendarPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/money" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><MoneyPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/supplies" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><SuppliesPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/health" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><HealthPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/documents" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><DocumentsPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/memories" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><MemoriesPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/family-plan" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><FamilyPlanPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/setup" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><HomeOSSetupPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/welcome" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><WelcomeRoomPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/contacts" element={<ProtectedRoute><HomeOSStateProvider><CalmModeProvider><ContactsPage /></CalmModeProvider></HomeOSStateProvider></ProtectedRoute>} />
      <Route path="/home-os/design" element={<ProtectedRoute><ResidenceJournalSpecimenPage /></ProtectedRoute>} />
      <Route path="/home-os/*" element={<Navigate to="/home-os" replace />} />
      
      {/* Legacy redirects — all sub-pages now live inside the 4 core sections */}
      <Route path="/home" element={<Navigate to="/home-os" replace />} />
      <Route path="/transactions" element={<Navigate to="/flow" replace />} />
      <Route path="/transfers" element={<Navigate to="/flow?tab=transfers" replace />} />
      <Route path="/review" element={<Navigate to="/close" replace />} />
      <Route path="/reconcile" element={<Navigate to="/close?tab=reconcile" replace />} />
      <Route path="/bills" element={<Navigate to="/plan?tab=expenses" replace />} />
      <Route path="/weekly-close" element={<Navigate to="/close" replace />} />
      <Route path="/income" element={<Navigate to="/plan?tab=income" replace />} />
      <Route path="/buckets" element={<Navigate to="/plan?tab=expenses" replace />} />
      <Route path="/budgets" element={<Navigate to="/plan?tab=expenses" replace />} />
      <Route path="/goals" element={<Navigate to="/plan" replace />} />
      <Route path="/goals-v2" element={<Navigate to="/plan" replace />} />
      <Route path="/cashflow" element={<Navigate to="/flow" replace />} />
      
      <Route path="/the-arc" element={
        <ProtectedRoute>
          <TheARCPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/integrations" element={
        <ProtectedRoute>
          <IntegrationsPage />
        </ProtectedRoute>
      } />
      <Route path="/rules" element={
        <ProtectedRoute>
          <RulesPage />
        </ProtectedRoute>
      } />
      <Route path="/circle" element={
        <ProtectedRoute>
          <CirclePage />
        </ProtectedRoute>
      } />
      <Route path="/game" element={
        <ProtectedRoute>
          <GamePage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/mentor" element={
        <ProtectedRoute>
          <MentorPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/vision" element={
        <ProtectedRoute>
          <VisionPanelPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/beta" element={
        <ProtectedRoute>
          <BetaDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/beta-insights" element={
        <ProtectedRoute>
          <BetaInsightsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute>
          <AdminAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedRoute>
          <AdminFeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <FounderDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RhythmWrapper({ children }) {
  const { household } = useAuth();
  return (
    <RhythmProvider householdId={household?.household_id}>
      <PrivacyProvider>
        {children}
      </PrivacyProvider>
    </RhythmProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <RhythmWrapper>
              <GuidanceProvider>
                <AppRouter />
                <WelcomeOverlay />
                <WhanauBottomNav />
                <Toaster position="top-right" richColors />
              </GuidanceProvider>
            </RhythmWrapper>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
